import {nullify, ResponseData} from '@dvrd/dvr-controls';
import IDate from '@dvrd/idate';
import React, {useCallback, useContext, useEffect, useRef, useState} from 'react';
import {ISO_DATE_FORMAT} from '../../util/constants';
import {PeriodLabel, SalesCategory, SalesColumn} from '../../util/enums';
import {ActivateHandle, OrderDirection} from '../../util/interfaces';
import {getSalesDashboardStatsV2, getSalesDashboardV2Products} from '../../util/requestSender';
import {GeneralSalesProduct, SalesDashboardFilterGroup, SalesDashboardStats} from '../../util/types';
import {createErrorMessage, deepClone} from '../../util/utils';
import {onSetCrumbs} from '../../util/events';
import {AppContext} from '../misc/appContext';
import SalesProductDetailsController from './productDetails/salesProductDetailsController';
import SalesDashboard from './salesDashboard';
import {showDialog} from '@dvrd/dvr-controls';
import {useTranslation} from 'react-i18next';

type LoadParams = {
    countryCodes?: string;
    accountIDs?: Set<string>;
    period?: PeriodLabel;
    customPeriodStart?: IDate | null;
    customPeriodEnd?: IDate | null;
    order?: SalesColumn;
    orderDirection?: OrderDirection;
    page?: number;
    limit?: number;
    filters?: Array<SalesDashboardFilterGroup>;
    showHiddenProducts?: boolean;
    search?: string;
};

export function getDatesFromPeriod(
    period: PeriodLabel,
    customStart: IDate | null,
    customEnd: IDate | null
): [IDate | null, IDate | null] {
    const today = new IDate(null).startOf('day');
    switch (period) {
        case PeriodLabel.CUSTOM:
            return [customStart, customEnd];
        case PeriodLabel.TODAY:
            return [today, today.clone().endOf('day')];
        case PeriodLabel.YESTERDAY: {
            const yesterday = today.clone().subtract(1, 'day');
            return [yesterday, yesterday.clone().endOf('day')];
        }
        case PeriodLabel.LAST_7_DAYS:
            return [today.clone().subtract(6, 'day'), today];
        case PeriodLabel.LAST_14_DAYS:
            return [today.clone().subtract(13, 'day'), today];
        case PeriodLabel.LAST_30_DAYS:
            return [today.clone().subtract(29, 'day'), today];
        case PeriodLabel.LAST_90_DAYS:
            return [today.clone().subtract(89, 'day'), today];
        case PeriodLabel.ALL:
            // Use a start date far enough in the past to get all data
            return [new IDate('2020-01-01'), today];
    }
}

function sanitizeFilters(filters: Array<SalesDashboardFilterGroup>): Array<SalesDashboardFilterGroup> {
    // Backwards loop to allow deleting items immediately
    for (let ix = filters.length - 1; ix >= 0; ix--) {
        const filterGroup = filters[ix];
        filterGroup.filters = filterGroup.filters.filter(filter => filter.valueSet);
        if (!filterGroup.filters.length) filters.splice(ix, 1);
    }
    return filters;
}

const initialFilters: Array<SalesDashboardFilterGroup> = [
    {field: SalesColumn.QUANTITY, filters: [], group_type: 'AND'},
    {field: SalesColumn.REVENUE, filters: [], group_type: 'AND'},
    {field: SalesColumn.ORGANIC_SALES, filters: [], group_type: 'AND'},
    {field: SalesColumn.RETURN_QUANTITY, filters: [], group_type: 'AND'},
    {field: SalesColumn.RETURN_REVENUE, filters: [], group_type: 'AND'},
    {field: SalesColumn.RETURN_COSTS, filters: [], group_type: 'AND'},
    {field: SalesColumn.RETURN_COMMISSION, filters: [], group_type: 'AND'},
    {field: SalesColumn.AD_SPENT, filters: [], group_type: 'AND'},
    {field: SalesColumn.SPONSORED_SALES, filters: [], group_type: 'AND'},
    {field: SalesColumn.ACOS, filters: [], group_type: 'AND'},
    {field: SalesColumn.CPC, filters: [], group_type: 'AND'},
    {field: SalesColumn.PROD_COSTS, filters: [], group_type: 'AND'},
    {field: SalesColumn.COMMISSION, filters: [], group_type: 'AND'},
    {field: SalesColumn.VAT, filters: [], group_type: 'AND'},
    {field: SalesColumn.PROFIT, filters: [], group_type: 'AND'},
    {field: SalesColumn.MARGIN, filters: [], group_type: 'AND'}
];

const columnMap: Record<SalesCategory, Array<SalesColumn>> = {
    [SalesCategory.SALES]: [SalesColumn.QUANTITY, SalesColumn.REVENUE, SalesColumn.ORGANIC_SALES],
    [SalesCategory.RETURNS]: [
        SalesColumn.RETURN_QUANTITY,
        SalesColumn.RETURN_REVENUE,
        SalesColumn.RETURN_COSTS,
        SalesColumn.RETURN_COMMISSION
    ],
    [SalesCategory.ADVERTISING]: [SalesColumn.AD_SPENT, SalesColumn.SPONSORED_SALES, SalesColumn.ACOS, SalesColumn.CPC],
    [SalesCategory.COSTS]: [SalesColumn.PROD_COSTS, SalesColumn.COMMISSION, SalesColumn.VAT],
    [SalesCategory.PROFIT]: [SalesColumn.PROFIT, SalesColumn.MARGIN],
    [SalesCategory.COMPARISONS]: []
};

export default function SalesDashboardController() {
    const {account} = useContext(AppContext).accountContext;
    const {t} = useTranslation();
    const [search, setSearch] = useState('');
    const [countryCodes, setCountryCodes] = useState<string>('NL,BE');
    const [accountIDs, setAccountIDs] = useState<Set<string>>(new Set([account!.id]));
    const [period, setPeriod] = useState<PeriodLabel>(PeriodLabel.LAST_30_DAYS);
    const [customPeriodStart, setCustomPeriodStart] = useState<IDate | null>(null);
    const [customPeriodEnd, setCustomPeriodEnd] = useState<IDate | null>(null);
    const [page, setPage] = useState(0);
    const [limit, setLimit] = useState(25);
    const [total, setTotal] = useState(0);
    const [order, setOrder] = useState<SalesColumn>(SalesColumn.TITLE);
    const [orderDirection, setOrderDirection] = useState<OrderDirection>(OrderDirection.ASC);
    const [filters, setFilters] = useState<Array<SalesDashboardFilterGroup>>(initialFilters.slice());
    const [stats, setStats] = useState<SalesDashboardStats | null>(null);
    const [comparingStats, setComparingStats] = useState<SalesDashboardStats | null>(null);
    const [products, setProducts] = useState<Array<GeneralSalesProduct>>([]);
    const [loadingStats, setLoadingStats] = useState(true);
    const [loadingProducts, setLoadingProducts] = useState(true);
    const [showHiddenProducts, setShowHiddenProducts] = useState(false);
    const productDetailsRef = useRef<ActivateHandle>(null);
    const searchDebounce = useRef<number | undefined>(undefined);

    // Refs for hiding columns
    const hiddenCategories = useRef<Set<SalesCategory>>(new Set());
    const hiddenColumns = useRef<Set<SalesColumn>>(new Set());

    function onChangeSearch(value: string) {
        clearSearchDebounce();
        setSearch(value);
        if (!value) {
            setPage(0);
            loadProducts({search: '', page: 0});
        } else {
            searchDebounce.current = window.setTimeout(() => {
                setPage(0);
                loadProducts({search: value, page: 0});
            }, 500);
        }
    }

    function onEnterSearch() {
        clearSearchDebounce();
        loadProducts();
    }

    const clearSearchDebounce = useCallback(() => {
        window.clearTimeout(searchDebounce.current);
        searchDebounce.current = undefined;
    }, []);

    /**
     * Returns a toggle handler that shows/hides all table cells belonging to the given category
     * by adding or removing the 'category-hidden' CSS class.
     * Uses direct DOM manipulation instead of React state to avoid re-rendering the entire table.
     * Also sets the category visibility in the hiddenCategories ref to apply visibility after some other re-render
     */
    function onChangeCategoryVisibility(category: SalesCategory) {
        return function (toggled: boolean) {
            if (hiddenCategories.current.has(category)) hiddenCategories.current.delete(category);
            else hiddenCategories.current.add(category);

            let cells: NodeListOf<Element>;
            if (category === SalesCategory.COMPARISONS) cells = document.querySelectorAll('div.comparison');
            else cells = document.querySelectorAll(`[data-category="${category}"]`);
            cells.forEach(cell => {
                cell.classList.toggle('category-hidden', !toggled);
            });
        };
    }

    /**
     * Shows/hides a single column and adjusts its parent category header's colspan accordingly.
     * If all columns within a category are hidden, the entire category is hidden via onChangeCategoryVisibility.
     * Uses direct DOM manipulation instead of React state to avoid re-rendering the entire table.
     * Also sets the column visibility in the hiddenCategories ref to apply visibility after some other re-render
     */
    function onChangeColumnVisibility(column: SalesColumn | null, selected: boolean) {
        if (!column) {
            // "Select all" option used
            return onShowAllColumns();
        }
        if (hiddenColumns.current.has(column)) hiddenColumns.current.delete(column);
        else hiddenColumns.current.add(column);

        const category = Object.keys(columnMap).find((key: SalesCategory) => columnMap[key].includes(column)) as
            | SalesCategory
            | undefined;
        if (category) {
            const header = document.querySelector(`th.head-main[data-category="${category}"]`);
            if (header) {
                const currentValue = header.getAttribute('colspan');
                if (currentValue) {
                    let nextValue: number;
                    if (selected) nextValue = Number(currentValue) + 1;
                    else nextValue = Number(currentValue) - 1;
                    onChangeCategoryVisibility(category)(nextValue > 0);
                    header.setAttribute('colspan', nextValue.toString());
                }
            }
        }
        const cells = document.querySelectorAll(`[data-column="${column}"]`);
        cells.forEach(cell => {
            cell.classList.toggle('column-hidden', !selected);
        });
    }

    function onShowAllColumns() {
        hiddenColumns.current.clear();
        for (const column in SalesColumn) {
            const category = Object.keys(columnMap).find((key: SalesCategory) =>
                columnMap[key].includes(column as SalesColumn)
            ) as SalesCategory | undefined;
            if (!category) continue;
            const header = document.querySelector(`th.head-main[data-category="${category}"]`);
            if (!header) continue;
            header.setAttribute('colspan', columnMap[category].length.toString());
            onChangeCategoryVisibility(category)(true);
            const cells = document.querySelectorAll(`[data-column="${column}"]`);
            cells.forEach(cell => {
                cell.classList.toggle('column-hidden', false);
            });
        }
    }

    function onExport() {
        // TODO Export to excel
    }

    function onChangeCountries(countryCodes: string) {
        setCountryCodes(countryCodes);
        setPage(0);
        loadAll({countryCodes: countryCodes, page: 0});
    }

    function onChangeProductVisibility(value: boolean) {
        setShowHiddenProducts(value);
        loadProducts({showHiddenProducts: value});
    }

    function onChangeAccounts(_: string | null, _selected: boolean, accountIDs: Array<string>) {
        const ids = new Set(accountIDs);
        setAccountIDs(ids);
        setPage(0);
        loadAll({accountIDs: ids, page: 0});
    }

    function onChangePeriod(range: [IDate, IDate], period: PeriodLabel | null) {
        if (!period) period = PeriodLabel.CUSTOM;
        const [startDate, endDate] = range;
        setPeriod(period);
        if (period === PeriodLabel.CUSTOM) {
            setCustomPeriodStart(startDate);
            setCustomPeriodEnd(endDate);
        }
        setPage(0);
        loadAll({period, page: 0, customPeriodStart: startDate, customPeriodEnd: endDate});
    }

    function onChangeOrder(_order: SalesColumn) {
        return function () {
            let direction: OrderDirection;
            if (_order === order) {
                if (orderDirection === OrderDirection.ASC) direction = OrderDirection.DESC;
                else direction = OrderDirection.ASC;
            } else {
                direction = _order === SalesColumn.TITLE ? OrderDirection.ASC : OrderDirection.DESC;
                setOrder(_order);
            }
            setOrderDirection(direction);
            setPage(0);
            loadProducts({order: _order, orderDirection: direction, page: 0});
        };
    }

    function onClearFilters() {
        setFilters(initialFilters.slice());
    }

    function onApplyFilter(filterGroup: SalesDashboardFilterGroup) {
        const _filters = deepClone(filters);
        let found: boolean = false;
        for (let ix = 0; ix < _filters.length; ix++) {
            if (_filters[ix].field !== filterGroup.field) continue;
            _filters.splice(ix, 1, filterGroup);
            found = true;
            break;
        }
        if (!found) _filters.push(filterGroup);
        setFilters(_filters);
        setPage(0);
        loadProducts({page: 0, filters: _filters});
    }

    function onChangePage(nextPage: number) {
        return function () {
            setPage(nextPage);
            loadProducts({page: nextPage});
        };
    }

    function onChangeLimit(limit: number) {
        setLimit(limit);
        setPage(0);
        loadProducts({limit, page: 0});
    }

    function onClickProduct(product: GeneralSalesProduct) {
        return function () {
            productDetailsRef.current?.activate(product, accountIDs, period, customPeriodStart, customPeriodEnd);
        };
    }

    async function loadAll(params?: LoadParams) {
        await loadStats(params);
        await loadProducts(params);
    }

    function loadStats(params?: LoadParams) {
        return new Promise<void>(resolve => {
            const _accountIDs = params?.accountIDs ?? accountIDs;
            const _period = params?.period ?? period;
            const _customStart = params?.customPeriodStart ?? customPeriodStart;
            const _customEnd = params?.customPeriodEnd ?? customPeriodEnd;
            const [startDate, endDate] = getDatesFromPeriod(_period, _customStart, _customEnd);
            const _countryCodes = (params?.countryCodes ?? countryCodes).split(',');
            if (!startDate || !endDate || !_accountIDs.size) return resolve();
            setLoadingStats(true);
            getSalesDashboardStatsV2({
                data: {
                    account_ids: Array.from(_accountIDs),
                    country_codes: _countryCodes,
                    end_date: endDate.format(ISO_DATE_FORMAT),
                    start_date: startDate.format(ISO_DATE_FORMAT)
                },
                callback: (data: ResponseData) => {
                    setLoadingStats(false);
                    if (data.success) {
                        setStats(data.stats);
                        setComparingStats(data.comparing_stats);
                    } else
                        showDialog(
                            createErrorMessage(
                                data.message ??
                                    t('salesDashboardV2.errorLoadTotals', 'Het laden van de totalen is niet gelukt.')
                            ),
                            t('salesDashboardV2.loadFailed', 'Laden mislukt')
                        );
                    resolve();
                }
            });
        });
    }

    function loadProducts(params?: LoadParams) {
        return new Promise<void>(resolve => {
            clearSearchDebounce();
            const _accountIDs = params?.accountIDs ?? accountIDs;
            const _period = params?.period ?? period;
            const _customStart = params?.customPeriodStart ?? customPeriodStart;
            const _customEnd = params?.customPeriodEnd ?? customPeriodEnd;
            const [startDate, endDate] = getDatesFromPeriod(_period, _customStart, _customEnd);
            if (!startDate || !endDate || !_accountIDs.size) return resolve();
            setLoadingProducts(true);
            const _order = params?.order ?? order;
            const _orderDirection = params?.orderDirection ?? orderDirection;
            const _countryCodes = (params?.countryCodes ?? countryCodes).split(',');
            const _page = params?.page ?? page;
            const _limit = params?.limit ?? limit;
            const _filters = sanitizeFilters(deepClone(params?.filters ?? filters));
            const _showHiddenProducts = params?.showHiddenProducts ?? showHiddenProducts;
            const _search = params?.search ?? search;
            getSalesDashboardV2Products({
                data: {
                    page: _page,
                    order: _order,
                    limit: _limit,
                    account_ids: Array.from(_accountIDs),
                    country_codes: nullify(Array.from(_countryCodes)),
                    end_date: endDate!.format(ISO_DATE_FORMAT),
                    start_date: startDate!.format(ISO_DATE_FORMAT),
                    search: nullify(_search),
                    filters: _filters,
                    order_direction: _orderDirection,
                    hidden_products: _showHiddenProducts
                },
                callback: (data: ResponseData) => {
                    setLoadingProducts(false);
                    if (data.success) {
                        setProducts(data.sales_products);
                        if (data.total > -1) setTotal(data.total);
                    } else
                        showDialog(
                            createErrorMessage(
                                data.message ??
                                    t(
                                        'salesDashboardV2.errorLoadProducts',
                                        'Het laden van de producten is niet gelukt.'
                                    )
                            ),
                            t('salesDashboardV2.loadFailed', 'Laden mislukt')
                        );
                    resolve();
                }
            });
        });
    }

    useEffect(() => {
        onSetCrumbs([{label: t('salesDashboardV2.breadcrumb', 'Sales Dashboard')}]);
        loadAll();
    }, []);

    return (
        <>
            <SalesDashboard
                onChangeSearch={onChangeSearch}
                onEnterSearch={onEnterSearch}
                onChangeCategoryVisibility={onChangeCategoryVisibility}
                onChangeColumnVisibility={onChangeColumnVisibility}
                onExport={onExport}
                onChangeCountries={onChangeCountries}
                onChangeAccounts={onChangeAccounts}
                onChangePeriod={onChangePeriod}
                onChangeOrder={onChangeOrder}
                onClearFilters={onClearFilters}
                onApplyFilter={onApplyFilter}
                onChangePage={onChangePage}
                onChangeLimit={onChangeLimit}
                onClickProduct={onClickProduct}
                onChangeProductVisibility={onChangeProductVisibility}
                search={search}
                countryCodes={countryCodes}
                accountIDs={accountIDs}
                period={period}
                customPeriodStart={customPeriodStart}
                customPeriodEnd={customPeriodEnd}
                page={page}
                limit={limit}
                total={total}
                order={order}
                filters={filters}
                products={products}
                loadingProducts={loadingProducts}
                stats={stats}
                comparingStats={comparingStats}
                loadingStats={loadingStats}
                hiddenCategories={hiddenCategories.current}
                hiddenColumns={hiddenColumns.current}
                showHiddenProducts={showHiddenProducts}
            />
            <SalesProductDetailsController ref={productDetailsRef} onReload={loadProducts} />
        </>
    );
}
